import { useEffect, useRef, useState } from 'react';

interface Tab {
  id: string;
  label: string;
}

interface MobileSectionTabsProps {
  tabs: Tab[];
  visible: boolean;
  headerVisible?: boolean;
}

export function MobileSectionTabs({ tabs, visible, headerVisible = false }: MobileSectionTabsProps) {
  const [active, setActive] = useState(tabs[0]?.id);
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    tabs.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActive(id);
        },
        { rootMargin: '-120px 0px -60% 0px', threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [tabs]);

  // Auto-scroll active tab into view
  useEffect(() => {
    if (!stripRef.current) return;
    const btn = stripRef.current.querySelector(`[data-tab-id="${active}"]`) as HTMLElement | null;
    if (btn) {
      const stripRect = stripRef.current.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const offset = btnRect.left - stripRect.left - stripRect.width / 2 + btnRect.width / 2;
      stripRef.current.scrollBy({ left: offset, behavior: 'smooth' });
    }
  }, [active]);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!visible) return null;

  return (
    <div
      className="lg:hidden fixed left-0 right-0 z-20 bg-background border-b transition-[top] duration-200"
      style={{ top: headerVisible ? '57px' : '0px' }}
    >
      <div ref={stripRef} className="flex overflow-x-auto no-scrollbar" style={{ scrollbarWidth: 'none' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-tab-id={tab.id}
            onClick={() => handleClick(tab.id)}
            className={`shrink-0 px-4 py-3 text-xs font-semibold whitespace-nowrap relative transition-colors ${
              active === tab.id ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            {tab.label}
            {active === tab.id && (
              <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
